import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Derive API URL dynamically so it works from localhost AND from phone (192.168.x.x)
const getApiUrl = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    const host = window.location.hostname;
    return `http://${host}:5000/api`;
};
const API_URL = getApiUrl();

const formatCurrency = (amount) => {
    const val = parseFloat(amount) || 0;
    let [intPart, decPart] = val.toFixed(2).split('.');
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${intPart},${decPart}`;
};

const DocumentVerifyPage = () => {
    const { id } = useParams();
    const [rapport, setRapport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchRapport = async () => {
            try {
                const res = await axios.get(`${API_URL}/verify/${id}`);
                setRapport(res.data.data);
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError(true);
                setLoading(false);
            }
        };
        fetchRapport();
    }, [id]);

    const formatDate = (d) => {
        if (!d) return '---';
        try {
            return new Date(d).toLocaleDateString('fr-DZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch { return d; }
    };

    if (loading) {
        return (
            <div style={styles.fullCenter}>
                <div style={{ textAlign: 'center' }}>
                    <div style={styles.spinner}></div>
                    <div style={{ color: '#64748b', marginTop: '15px', fontSize: '14px' }}>Vérification en cours...</div>
                </div>
                <style>{spinnerCSS}</style>
            </div>
        );
    }

    if (error || !rapport) {
        return (
            <div style={{ ...styles.fullCenter, background: '#fff1f2' }}>
                <div style={styles.card}>
                    <div style={{ ...styles.iconCircle, background: '#fee2e2' }}>
                        <XIcon />
                    </div>
                    <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#111827', margin: '16px 0 8px' }}>Document Non Authentifié</h2>
                    <p style={{ color: '#6b7280', lineHeight: 1.6, maxWidth: '320px', margin: '0 auto' }}>
                        Aucune trace de ce rapport dans nos registres officiels. Veuillez contacter directement l'expert pour vérification.
                    </p>
                </div>
            </div>
        );
    }

    const pieces = rapport.pieces || [];

    return (
        <div style={styles.page}>
            {/* Brand Header */}
            <div style={styles.brandHeader}>
                <div style={styles.brandLogo}>🛡️</div>
                <div>
                    <div style={styles.brandTitle}>HabibExpertiseSoft</div>
                    <div style={styles.brandSub}>Portail d'Authentification Officielle des Documents</div>
                </div>
            </div>

            {/* Card */}
            <div style={styles.card}>
                {/* Green checkmark section */}
                <div style={styles.authSection}>
                    <div style={styles.checkWrap}>
                        <CheckIcon />
                        <div style={styles.shieldBadge}><ShieldIcon /></div>
                    </div>
                    <h2 style={styles.authTitle}>Document Authentique</h2>
                    <div style={styles.certBadge}>✅ CERTIFIÉ — M. HABIB BRADAI, EXPERT AGRÉÉ U.A.R</div>
                </div>

                {/* ─── IDENTIFICATION ─── */}
                <div style={styles.detailsSection}>
                    <div style={styles.sectionLabel}>Identification du Rapport</div>
                    <div style={styles.grid}>
                        <DataRow label="N° Rapport" value={rapport.numero_rapport} highlight />
                        <DataRow label="Date d'Expertise" value={formatDate(rapport.date_rapport)} />
                        <DataRow label="N° Sinistre" value={rapport.numero_sinistre} />
                        <DataRow label="N° Police" value={rapport.numero_police} />
                        <DataRow label="Sinistré" value={rapport.nom_assure} />
                        <DataRow label="Véhicule" value={`${rapport.marque_vehicule || ''} ${rapport.modele_vehicule || ''}`.trim() || '---'} />
                        <DataRow label="Immatriculation" value={rapport.immatriculation} mono />
                        <DataRow label="Demandeur" value={rapport.nom_assurance} full />
                        <DataRow label="Code Assurance" value={rapport.code_assurance} />
                        <DataRow label="Ville" value={rapport.ville_assurance} />
                    </div>
                </div>

                {/* ─── CONSTATATIONS ─── */}
                {rapport.constatations && (
                    <div style={styles.detailsSection}>
                        <div style={styles.sectionLabel}>Constatations Techniques</div>
                        <div style={styles.constatBox}>
                            {rapport.constatations}
                        </div>
                    </div>
                )}

                {/* ─── FOURNITURES ─── */}
                {pieces.length > 0 && (
                    <div style={styles.detailsSection}>
                        <div style={styles.sectionLabel}>Fournitures</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {pieces.map((p, i) => (
                                <div key={i} style={styles.pieceCard}>
                                    <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '12px', marginBottom: '6px' }}>{p.designation}</div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                        <span style={styles.pieceTag}>Qté: {Math.round(parseFloat(p.quantite) || 1)}</span>
                                        <span style={{ ...styles.pieceTag, background: '#e0f2fe', color: '#1e3a8a', fontWeight: '800' }}>Total: {formatCurrency(p.sous_total)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ─── FINANCIALS ─── */}
                <div style={styles.detailsSection}>
                    <div style={styles.sectionLabel}>Évaluation Financière</div>
                    <div style={styles.finGrid}>
                        <FinRow label="A/ Fournitures" value={formatCurrency(rapport.total_pieces)} />
                        <FinRow label="B/ Peinture et Matière" value={formatCurrency(rapport.peinture)} />
                        <FinRow label="C/ Main d'Œuvre" value={formatCurrency(rapport.main_oeuvre)} />
                        {parseFloat(rapport.vetuste_montant) > 0 && (
                            <FinRow label="D/ Moins Vétusté (-)" value={`- ${formatCurrency(rapport.vetuste_montant)}`} negative />
                        )}
                        <div style={styles.totalRow}>
                            <span style={{ fontWeight: '800', fontSize: '12px' }}>TOTAL GLOBAL</span>
                            <span style={{ fontWeight: '900', fontSize: '14px', color: '#064e3b' }}>{formatCurrency(rapport.total_general)}</span>
                        </div>
                    </div>
                </div>

                {/* ─── METRICS ─── */}
                <div style={styles.detailsSection}>
                    <div style={styles.sectionLabel}>Informations Complémentaires</div>
                    <div style={styles.metricsGrid}>
                        <MetricBox label="Valeur Vénale" value={formatCurrency(rapport.valeur_venale)} />
                        <MetricBox label="N. / Photos" value={rapport.nb_photos || '0'} />
                        <MetricBox label="Immobilisation" value={`${rapport.jours_immobilisation || 0} Jours`} />
                        <MetricBox label="Vétusté" value={`${rapport.vetuste || 0} %`} />
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <span>ID de Certification : </span>
                    <span style={{ fontFamily: 'monospace', color: '#334155' }}>{rapport.id}</span>
                    <div style={{ marginTop: '6px' }}>
                        Ce document a été validé et certifié authentique au moment de son émission.
                    </div>
                </div>
            </div>

            <div style={styles.copyright}>© 2026 EXPERTISE HB CRM • Tous droits réservés</div>
        </div>
    );
};

/* ─── Sub-components ─────────────────────── */
const DataRow = ({ label, value, highlight, mono, full }) => (
    <div style={{ gridColumn: full ? 'span 2' : 'span 1', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <div style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
        <div style={{ fontSize: '13px', fontWeight: highlight ? '800' : '600', color: highlight ? '#1e3a8a' : '#1e293b', fontFamily: mono ? 'monospace' : 'inherit', letterSpacing: mono ? '0.5px' : 'normal' }}>
            {value || '---'}
        </div>
    </div>
);

const FinRow = ({ label, value, negative }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #e2e8f0' }}>
        <span style={{ fontSize: '12px', color: negative ? '#dc2626' : '#475569', fontWeight: '600' }}>{label}</span>
        <span style={{ fontSize: '12px', color: negative ? '#dc2626' : '#1e293b', fontWeight: '700', fontFamily: 'monospace' }}>{value}</span>
    </div>
);

const MetricBox = ({ label, value }) => (
    <div style={{ textAlign: 'center', padding: '10px 6px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '13px', fontWeight: '800', color: '#1e3a8a' }}>{value}</div>
    </div>
);

const CheckIcon = () => (
    <div style={{ width: '80px', height: '80px', background: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px rgba(16,185,129,0.25)' }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
    </div>
);

const ShieldIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="none">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);

const XIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

/* ─── Styles ─────────────────────────────── */
const styles = {
    page: { minHeight: '100vh', background: 'linear-gradient(135deg, #e0f2fe 0%, #f0fdf4 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '30px 16px' },
    brandHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', textAlign: 'left' },
    brandLogo: { fontSize: '32px' },
    brandTitle: { fontSize: '17px', fontWeight: '800', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.5px' },
    brandSub: { fontSize: '11px', color: '#3b82f6', fontWeight: '600' },
    card: { maxWidth: '620px', width: '100%', background: 'white', borderRadius: '28px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.08)', overflow: 'hidden', border: '1px solid #e2e8f0' },
    authSection: { background: '#f0fdf4', padding: '36px 24px 28px', textAlign: 'center', borderBottom: '1px solid #d1fae5' },
    checkWrap: { position: 'relative', display: 'inline-block', marginBottom: '14px' },
    shieldBadge: { position: 'absolute', bottom: -4, right: -4, background: '#10b981', padding: '5px', borderRadius: '50%', border: '3px solid #f0fdf4', display: 'flex' },
    authTitle: { fontSize: '26px', fontWeight: '900', color: '#064e3b', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    certBadge: { display: 'inline-block', background: '#10b981', color: 'white', padding: '6px 16px', borderRadius: '40px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.3px' },
    detailsSection: { padding: '20px 24px 10px' },
    sectionLabel: { fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px', borderBottom: '1.5px solid #f1f5f9', paddingBottom: '8px' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    constatBox: { fontSize: '11px', lineHeight: 1.6, color: '#334155', background: '#f8fafc', padding: '12px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', whiteSpace: 'pre-wrap', maxHeight: '150px', overflow: 'auto' },
    pieceCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 14px' },
    pieceTag: { fontSize: '10px', fontWeight: '600', color: '#475569', background: '#f1f5f9', padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' },
    finGrid: { background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '12px 16px' },
    totalRow: { display: 'flex', justifyContent: 'space-between', padding: '12px 0 4px', borderTop: '2px solid #1e3a8a', marginTop: '4px', color: '#1e3a8a' },
    metricsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
    footer: { padding: '16px 24px 20px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', color: '#94a3b8', fontSize: '10px', textAlign: 'center', lineHeight: 1.6 },
    fullCenter: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: '20px' },
    iconCircle: { width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' },
    copyright: { marginTop: '24px', color: '#94a3b8', fontSize: '11px' },
    spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#10b981', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' },
};

const spinnerCSS = `@keyframes spin { to { transform: rotate(360deg); } }`;

export default DocumentVerifyPage;
